import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../assets/images/e-guruji-logo.png";
import "./style.css";
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SettingsIcon from '@mui/icons-material/Settings';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AddHomeWorkIcon from '@mui/icons-material/AddHomeWork';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { Link } from "react-router-dom";
import NavigationSearchBar from "./NavigationSearchBar";

const DB_NAME = "SchoolManagementDB";
const OBJECT_STORE_NAME = "studentData";

// Function to clear all object stores and delete IndexedDB database on logout
const deleteIndexedDB = () => {
  return new Promise((resolve) => {
    const openRequest = indexedDB.open(DB_NAME);

    openRequest.onerror = () => {
      indexedDB.deleteDatabase(DB_NAME);
      resolve();
    };

    openRequest.onsuccess = (event) => {
      const db = event.target.result;
      const storeNames = Array.from(db.objectStoreNames);

      if (storeNames.length === 0) {
        db.close();
        indexedDB.deleteDatabase(DB_NAME);
        return resolve();
      }

      try {
        const transaction = db.transaction(storeNames, "readwrite");
        storeNames.forEach((storeName) => {
          transaction.objectStore(storeName).clear();
        });

        transaction.oncomplete = () => {
          db.close();
          indexedDB.deleteDatabase(DB_NAME);
          console.log("All IndexedDB stores cleared successfully on logout.");
          resolve();
        };

        transaction.onerror = (err) => {
          console.error("Error clearing stores on logout:", err);
          db.close();
          indexedDB.deleteDatabase(DB_NAME);
          resolve();
        };
      } catch (err) {
        console.error("Error in logout transaction:", err);
        db.close();
        indexedDB.deleteDatabase(DB_NAME);
        resolve();
      }
    };
  });
};

// Function to open IndexedDB and get all students from object store
const getAllStudents = () => {
  return new Promise((resolve, reject) => {
    const openRequest = indexedDB.open(DB_NAME);

    openRequest.onerror = (event) => {
      console.error("Error opening IndexedDB:", event.target.error);
      reject(event.target.error);
    };

    openRequest.onsuccess = (event) => {
      const db = event.target.result;
      const transaction = db.transaction(OBJECT_STORE_NAME, "readonly");
      const objectStore = transaction.objectStore(OBJECT_STORE_NAME);
      const getAllRequest = objectStore.getAll();

      getAllRequest.onerror = (event) => {
        console.error("Error reading students from IndexedDB:", event.target.error);
        reject(event.target.error);
      };

      getAllRequest.onsuccess = (event) => {
        resolve(event.target.result);
      };
    };
  });
};

// Function to open IndexedDB
const openIndexedDB = () => {
  return new Promise((resolve, reject) => {
    const openRequest = indexedDB.open(DB_NAME);

    openRequest.onerror = (event) => {
      console.error("Error opening IndexedDB:", event.target.error);
      reject(event.target.error);
    };

    openRequest.onsuccess = (event) => {
      resolve(event.target.result);
    };
  });
};
// Function to fetch student data by ID
const fetchStudentData = async (studentId) => {
  const db = await openIndexedDB();
  const transaction = db.transaction(OBJECT_STORE_NAME, "readonly");
  const objectStore = transaction.objectStore(OBJECT_STORE_NAME);
  const request = objectStore.get(studentId);

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      resolve(event.target.result);

    };
    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

const Navbar = ({ setStdData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(localStorage.getItem("language") || "English");
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [students, setStudents] = useState([]);
  const [matchedResults, setMatchedResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  // const department = localStorage.getItem("department") || "";
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedStudentIndex, setSelectedStudentIndex] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState(localStorage.getItem("selectedStudentName") || "");


  const panelRef = useRef();

  // Log when the component mounts
  useEffect(() => {
    console.log("Navbar component mounted");
  }, []);
  // Log when the panel opens or closes
  useEffect(() => {
    console.log("Panel state changed:", isPanelOpen);
  }, [isPanelOpen]);


  useEffect(() => {
    const storedLanguage = localStorage.getItem("language") || "English";
    setLanguage(storedLanguage);
  }, []);

  useEffect(() => {
    if (searchVisible) {
      getAllStudents()
        .then((data) => {
          console.log("Fetched student data from IndexedDB:", data);
          setStudents(data || []);
          setMatchedResults([]);
        })
        .catch((err) => {
          console.error("Failed to load students:", err);
        });
    }
  }, [searchVisible]);


  const togglePanel = () => {
    setIsPanelOpen(prev => !prev);
    console.log("Toggled panel visibility:", !isPanelOpen);
  };


  const handleLogout = async () => {
    setIsPanelOpen(false)
    localStorage.clear();
    await deleteIndexedDB();
    navigate("/");
  };


  const toggleSearch = () => {
    setSearchVisible(!searchVisible);
    setSearchQuery("");
    setMatchedResults([]);
    if (searchVisible) {
      setSelectedStudent(null); // Clear selected student when closing search
    }
  };

  const highlightText = (text, query) => {
    if (!text || !query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: "#ffeb3b", fontWeight: "bold" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };



  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsPanelOpen(false);
      }
    };
    if (isPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPanelOpen]);

  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase(); // Removed trim() to allow spaces
    setSearchQuery(event.target.value); // Preserve original input for display

    if (query === "") {
      setMatchedResults([]);
      return;
    }

    const filtered = students
      .map((student) => {
        const matches = {};
        const fields = [
          "stdName",
          "stdFather",
          "stdSurname",
          "stdAdharNo",
          "taluka",
          "registerNo",
          "state",
          "currentClass",
          "casteCategory",
          "nationality",
          "panId",
          "caste",
          "studentId",
          "mobileNo",
          "IFSC",
          "accNo",
          "birthPlace",
          "dob",
          "district",
          "stdMother",
        ];

        fields.forEach((field) => {
          if (student[field] && student[field].toLowerCase().includes(query)) {
            matches[field] = student[field];
          }
        });

        return Object.keys(matches).length > 0 ? { ...student, matches } : null;
      })
      .filter((student) => student !== null);

    setMatchedResults(filtered);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchChange(event);
    }
  };

  const handleStudentSelect = async (student, index) => {
    try {
      toggleSearch();
      const studentData = await fetchStudentData(student.id);
      console.log("Fetched student data:", studentData);
      if (!studentData) {
        console.warn("No student data returned for ID:", student.id);
        return;
      }

      const serialNo = studentData.serialNo || index + 1;
      const fullName = `${studentData.stdName || ""} ${studentData.stdFather || ""} ${studentData.stdSurname || ""}`.trim();
      localStorage.setItem("selectedStudentSerialNo", serialNo);
      localStorage.setItem("selectedStudentId", student.id);
      localStorage.setItem("selectedStudentName", fullName);
      console.log(`Saved serial number ${serialNo}, ID ${student.id}, and name ${fullName} to localStorage`);

      if (typeof setStdData === "function") {
        setStdData(studentData);
      } else {
        console.warn("setStdData is not a function. Received:", setStdData);
      }

      window.location.reload();
    } catch (error) {
      console.error("Error fetching student data:", error);
      toggleSearch();
      const fullName = `${student.stdName || ""} ${student.stdFather || ""} ${student.stdSurname || ""}`.trim();
      localStorage.setItem("selectedStudentSerialNo", index + 1);
      localStorage.setItem("selectedStudentId", student.id);
      localStorage.setItem("selectedStudentName", fullName);
      console.log(`Fallback: Saved serial number ${index + 1}, ID ${student.id}, and name ${fullName} to localStorage`);

      window.location.reload();
    }
  };
  const handleClearSerialNo = () => {
    localStorage.removeItem("selectedStudentSerialNo");
    localStorage.removeItem("selectedStudentName");
    localStorage.removeItem("selectedStudentId");
    setSelectedStudentName("");
    setSelectedStudent(null);
    console.log("Cleared selectedStudentSerialNo, selectedStudentName, and selectedStudentId from localStorage");
    window.location.reload();
  };

  const settingClick = () => {
    navigate('/defaultsetting')
  }

  return (
    <div
      className="Navbar fixed-top"
      style={{
        backgroundColor: "#004d99",
        padding: "10px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
      }}
    >
      <div className="leftSide" style={{ display: "flex", alignItems: "center" }}>
        <img
          className="rounded-circle"
          src={Logo}
          alt="School Logo"
          style={{ height: 40, width: 40, marginRight: 10 }}
        />
        <span
          className="eguruji-heading"
          style={{ color: "white", fontWeight: "bold", fontSize: 20 }}
        >
          SGK Brainova
        </span>
      </div>

      <div className="rightSide" style={{ display: "flex", alignItems: "center" }}>
     

        {location.pathname !== "/" &&
          location.pathname !== "/school-register" &&
          location.pathname !== "/eguruji-landingpage" &&
          location.pathname !== "/downloadexe-landingpage" && (
            <>
              <NavigationSearchBar />

              {localStorage.getItem("selectedStudentSerialNo") && selectedStudentName && (
                <div style={{
                  display: "flex", alignItems: "center",
                  border: '1px solid white',
                  borderRadius: '6px', padding: '4px', backgroundColor: 'rgb(44 120 195 / 97%)'
                }}>

                  <span
                    style={{
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "500",
                      userSelect: "none",
                      marginRight: "5px",
                    }}
                    title={language === "English" ? "Selected Student" : "निवडलेला विद्यार्थी"}
                  >
                    {language === "English" ? `Student: ` : `विद्यार्थी: `}{selectedStudentName}
                  </span>
                  <button
                    onClick={handleClearSerialNo}
                    style={{
                      backgroundColor: "#ff4d4d",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "5px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                      transition: "background-color 0.2s",
                      padding: '10px'
                    }}
                    title={language === "English" ? "Remove Selected Student" : "निवडलेला विद्यार्थी हटवा"}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = "#cc0000")}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = "#ff4d4d")}
                  >
                    ✕
                  </button>
                </div>
              )}


              <span
                onClick={() => setIsPanelOpen(prev => !prev)}
                style={{
                  color: "white",
                  padding: "2px 5px 3px 5px",
                  border: "1px solid white",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "bolder",
                  fontSize: '1.125rem',
                }}
              >
                <SettingsIcon />
              </span>
            </>
          )}
      </div>

      <style>
        {`
  .panel-open {
  transform: translateX(0) !important;
  opacity: 1 !important;
  pointer-events: auto !important;
}

.panel-closed {
  transform: translateX(100%) !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
.panel {
  position: fixed;
  top: 66px;
  right: 10px;
  width: 80px;
  height: max-content;
  background-color:rgba(204, 223, 243, 0.8);
  {/* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4); */}
  box-shadow: -10px 0px 30px 10px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 20px 10px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  transform: translateX(100%);
  opacity: 0;
  pointer-events: none; text-align: center;
}



`}

      </style>

      {isPanelOpen && (
        <div
          ref={panelRef}
          className={`panel ${isPanelOpen ? "panel-open" : "panel-closed"}`}
        >



          <button
            style={{
              backgroundColor: "#004d99",
              color: "#ffffff",
              padding: "6px", height: '40px', width: '40px',
              borderRadius: "50%",
              border: "none",
              marginBottom: "7px",
              cursor: "pointer",
              fontWeight: "bold", marginLeft: 'auto', marginRight: 'auto'
            }}
          >
            <Link
              to="/plans"
              style={{ color: 'inherit', textDecoration: 'none', outline: 'none' }}
            >
              <CurrencyRupeeIcon style={{ verticalAlign: 'middle', marginRight: 8, color: 'inherit' }} />
            </Link>
          </button>
          <p style={{ fontSize: '12px', width: '100%' }}>Plans</p>




          <button
            style={{
              backgroundColor: "#004d99",
              color: "#ffffff",
              padding: "6px", height: '40px', width: '40px',
              borderRadius: "50%",
              border: "none",
              marginBottom: "7px",
              cursor: "pointer",
              fontWeight: "bold", marginLeft: 'auto', marginRight: 'auto'
            }}
          >
            <Link
              to="/school-data"
              style={{ color: 'inherit', textDecoration: 'none', outline: 'none' }}
            >
              <AddHomeWorkIcon style={{ verticalAlign: 'middle', marginRight: 8, color: 'inherit' }} />
            </Link>

          </button>
          <p style={{ fontSize: '12px', width: '100%' }}>Profile</p>



          <button
            onClick={toggleSearch}
            style={{
              backgroundColor: "#004d99",
              color: "#ffffff",
              padding: "6px", height: '40px', width: '40px',
              borderRadius: "50%",
              border: "none",
              marginBottom: "7px",
              cursor: "pointer",
              fontWeight: "bold", marginLeft: 'auto', marginRight: 'auto'
            }}
          >
            <PersonSearchIcon style={{ verticalAlign: "middle", marginRight: "8px" }} />

          </button>
          <p style={{ fontSize: '12px', width: '100%' }}>Search</p>

          <button
            onClick={settingClick}
            style={{
              backgroundColor: "#004d99",
              color: "#ffffff",
              padding: "6px", height: '40px', width: '40px',
              borderRadius: "50%",
              border: "none",
              marginBottom: "7px",
              cursor: "pointer",
              fontWeight: "bold", marginLeft: 'auto', marginRight: 'auto'
            }}
          >
            <SettingsIcon style={{ verticalAlign: "middle", marginRight: "8px" }} />

          </button>
          <p style={{ fontSize: '12px', width: '100%' }}>Setting</p>

          <button
            onClick={() => navigate('/whatsapp')}
            style={{
              backgroundColor: "#004d99",
              color: "#ffffff",
              padding: "6px", height: '40px', width: '40px',
              borderRadius: "50%",
              border: "none",
              marginBottom: "7px",
              cursor: "pointer",
              fontWeight: "bold", marginLeft: 'auto', marginRight: 'auto'
            }}
          >
            <WhatsAppIcon style={{ verticalAlign: "middle" }} />
          </button>
          <p style={{ fontSize: '12px', width: '100%' }}>WhatsApp</p>

          <button
            onClick={handleLogout}
            style={{
              backgroundColor: "red",
              color: "#ffffff", padding: "6px", height: '40px', width: '40px',
              borderRadius: "50%",
              border: "none",
              cursor: "pointer",
              marginBottom: "7px",
              fontWeight: "bold", marginLeft: 'auto', marginRight: 'auto'
            }}
          >
            <PowerSettingsNewIcon />

          </button>
          <p style={{ fontSize: '12px', width: '100%' }}>{language === "English" ? "Log Out" : "लॉग आउट"}</p>

        </div>
      )}




      {searchVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            width: "100vw",
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "20px",
          }}
          onClick={toggleSearch}
        >
          <div
            style={{
              position: "relative",
              top: "80px",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
              width: "90%",
              maxWidth: "1200px",
              maxHeight: "80vh",
              overflowY: "auto",
              padding: "20px",
              animation: "fadeIn 0.3s ease-in-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={toggleSearch}
              style={{
                position: "absolute",
                top: "30px",
                right: "25px",
                backgroundColor: "#ff4d4d",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer", padding: '2px 0px',
                fontSize: "18px",
                transition: "background-color 0.2s",
              }}
              title={language === "English" ? "Close Search" : "बंद करा"}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#cc0000")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#ff4d4d")}
            >
              ✕
            </button>
            <input
              type="text"
              placeholder={
                language === "English"
                  ? "Search by students Name, Father Name, Surname, Aadhaar, Taluka, Register No..."
                  : "विद्यार्थ्याचे नाव, वडिलांचे नाव, आडनाव, आधार नंबर, तालुका, रजिस्टर नंबर ने शोधा..."
              }
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              style={{
                width: "100%",
                margin: "0 0 15px 0",
                padding: "12px",
                fontSize: "16px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)",
                outline: "none",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#004d99")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
              autoFocus
            />
            {matchedResults.length === 0 && searchQuery !== "" && (
              <div
                style={{
                  padding: "15px",
                  color: "#6b7280",
                  fontSize: "16px",
                  textAlign: "center",
                  backgroundColor: "#f9fafb",
                  borderRadius: "6px",
                }}
              >
                {language === "English" ? "No results found" : "परिणाम मिळाले नाही"}
              </div>
            )}

            {matchedResults.length > 0 && (
              <div
                style={{
                  width: "100%",
                  overflowX: "auto",
                  padding: "10px 0",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    minWidth: "1200px",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    fontFamily: "'Roboto', sans-serif",
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <thead>
                    <tr>
                      {[
                        "Student Name",
                        "Register No",
                        "Aadhaar No",
                        "Taluka",
                        "District",
                        "Father",
                        "Mother",
                        "Current Class",
                        "Category",
                        "State",
                        "Nationality",
                        "PEN No",
                        "Caste",
                        "SARAL ID",
                        "Mobile No",
                        "IFSC Code",
                        "Account No",
                        "Birth Place",
                        "Date Of Birth",
                      ].map((header, index) => (
                        <th
                          key={index}
                          style={{
                            padding: "14px 16px",
                            backgroundColor: "#004d99",
                            color: "#ffffff",
                            textAlign: "left",
                            fontWeight: "600",
                            fontSize: "14px",
                            borderBottom: "2px solid #003366",
                            whiteSpace: "nowrap",
                            transition: "background-color 0.2s",
                            cursor: "default",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#003366")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "#004d99")
                          }
                        >
                          {language === "English"
                            ? header
                            : {
                              "Student Name": "विद्यार्थ्याचे नाव",
                              "Register No": "रजि. नं.",
                              'Aadhaar No': "आधार नं.",
                              Taluka: "तालुका",
                              District: "जिल्हा",
                              Father: "वडील",
                              Mother: "आई",
                              "Current Class": "सध्याचा वर्ग",
                              Category: "श्रेणी",
                              State: "राज्य",
                              Nationality: "राष्ट्रीयत्व",
                              "PEN No": "PEN No",
                              Caste: "जात",
                              'SARAL ID': "सरल आईडी",
                              "Mobile No": "मोबाइल नं.",
                              "IFSC Code": "IFSC Code",
                              "Account No": "खाते क्र",
                              "Birth Place": "जन्मस्थान",
                              "Date Of Birth": "जन्मतारीख",
                            }[header]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matchedResults.map((student, index) => (
                      <tr
                        key={index}
                        onClick={() => handleStudentSelect(student, index)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                          transition: "background-color 0.2s",
                          position: "relative",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e6f0fa";
                          if (selectedStudentIndex === index && searchVisible) {
                            e.currentTarget.setAttribute("data-hover-text", language === "English" ? "Link Student" : "छात्र लिंक करें");
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#f8fafc";
                          e.currentTarget.removeAttribute("data-hover-text");
                        }}
                      >
                        <td
                          style={{
                            padding: "14px 16px",
                            borderBottom: "1px solid #e5e7eb",
                            color: "#1f2937",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {highlightText(
                            `${student.stdName || ""} ${student.stdFather || ""} ${student.stdSurname || ""}`.trim(),
                            searchQuery
                          )}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.registerNo ? highlightText(student.registerNo, searchQuery) : student.registerNo}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.stdAdharNo ? highlightText(student.stdAdharNo, searchQuery) : student.stdAdharNo}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.taluka ? highlightText(student.taluka, searchQuery) : student.taluka}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.district ? highlightText(student.district, searchQuery) : student.district}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.stdFather ? highlightText(student.stdFather, searchQuery) : student.stdFather}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.stdMother ? highlightText(student.stdMother, searchQuery) : student.stdMother}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.currentClass ? highlightText(student.currentClass, searchQuery) : student.currentClass}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.casteCategory ? highlightText(student.casteCategory, searchQuery) : student.casteCategory}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.state ? highlightText(student.state, searchQuery) : student.state}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.nationality ? highlightText(student.nationality, searchQuery) : student.nationality}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.panId ? highlightText(student.panId, searchQuery) : student.panId}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.caste ? highlightText(student.caste, searchQuery) : student.caste}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.studentId ? highlightText(student.studentId, searchQuery) : student.studentId}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.mobileNo ? highlightText(student.mobileNo, searchQuery) : student.mobileNo}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.IFSC ? highlightText(student.IFSC, searchQuery) : student.IFSC}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.accNo ? highlightText(student.accNo, searchQuery) : student.accNo}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.birthPlace ? highlightText(student.birthPlace, searchQuery) : student.birthPlace}
                        </td>
                        <td style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "14px", whiteSpace: "nowrap" }}>
                          {student.matches.dob ? highlightText(student.dob, searchQuery) : student.dob}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};
export default Navbar;
